import React, { useEffect, useState } from "react";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api, { describeAxiosError } from "../lib/api";
import Avatar from "../components/app/Avatar";
import CountUp from "../components/app/CountUp";
import { toast } from "sonner";
import {
  Building2, Users, UserPlus, Trash2, Calendar, MapPin, Trophy, Sparkles, Mail,
  Inbox, Phone, Mail as MailIcon, MessageCircle, Check, Archive,
} from "lucide-react";

/**
 * Premium dashboard for users with role=academy.
 *  - Hero with academy branding (logo + name)
 *  - 4 stat tiles (coaches, players, upcoming sessions, revenue)
 *  - Coach roster with avatar + city + specialty + rate + remove
 *  - Invite-by-email form
 *  - Upcoming sessions list (across all member coaches)
 */
export default function AcademyDashboard() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteOk, setInviteOk] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/academy/overview");
      setData(data);
    } catch (e) {
      setData({ error: describeAxiosError(e) });
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setInviteBusy(true); setInviteError(""); setInviteOk("");
    try {
      await api.post("/academy/coaches", { email: inviteEmail.trim().toLowerCase() });
      setInviteOk(lang === "bg" ? "Треньорът е добавен" : "Coach added");
      setInviteEmail("");
      await load();
    } catch (e) {
      setInviteError(describeAxiosError(e));
    }
    setInviteBusy(false);
  };

  const removeCoach = async (id) => {
    if (!window.confirm(lang === "bg" ? "Премахни този треньор от академията?" : "Remove this coach?")) return;
    try { await api.delete(`/academy/coaches/${id}`); await load(); } catch (_) {}
  };

  if (!data) return <Layout><div className="min-h-[50vh] flex items-center justify-center"><div className="pitch-loader"><div className="ball" /></div></div></Layout>;

  const stats = data.stats || {};
  const coaches = data.coaches || [];
  const upcoming = data.upcoming_sessions || [];
  const playersCount = data.players_count || 0;
  const applications = data.applications || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="academy-dashboard">
        {/* Hero */}
        <div className="relative card-sharp overflow-hidden grain animate-fade-up" style={{ opacity: 0 }}>
          <div className="spotlight" />
          <div className="absolute inset-0 pitch-field opacity-40" />
          <div className="relative p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start">
            <div className="w-20 h-20 rounded-2xl bg-pitch/10 border-2 border-pitch/40 flex items-center justify-center flex-shrink-0">
              {user?.academy_logo ? (
                <img src={user.academy_logo} alt="" className="w-full h-full object-cover rounded-2xl" />
              ) : (
                <Building2 className="w-10 h-10 text-pitch" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-mono text-pitch mb-2">// ACADEMY COMMAND CENTER</div>
              <h1 className="font-oswald uppercase text-3xl sm:text-5xl tracking-tight leading-none truncate">
                {user?.academy_name || user?.name}
              </h1>
              {user?.academy_description && (
                <p className="text-white/55 mt-3 max-w-2xl text-sm">{user.academy_description}</p>
              )}
            </div>
            {user?.academy_size && (
              <span className="chip-badge">{user.academy_size}</span>
            )}
          </div>
        </div>

        {/* Stat tiles — Academies are lead-gen organizations on this platform,
            so Revenue/Earnings is replaced by an Applications inbox count. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile label={lang === "bg" ? "Треньори" : "Coaches"} value={stats.coaches || 0} icon={Users} delay={0} />
          <StatTile label={lang === "bg" ? "Играчи" : "Players"}   value={playersCount}     icon={Trophy} delay={50} />
          <StatTile label={lang === "bg" ? "Предстоящи" : "Upcoming"} value={stats.upcoming || 0} icon={Calendar} delay={100} />
          <StatTile label={lang === "bg" ? "Нови заявки" : "New leads"} value={stats.applications_new || 0} icon={Inbox} delay={150} accent />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4">
          {/* Coaches roster */}
          <div className="card-sharp p-5 animate-fade-up" style={{ animationDelay: "60ms", opacity: 0 }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-oswald uppercase text-2xl tracking-tight flex items-center gap-2">
                <Users className="w-5 h-5 text-pitch" /> {lang === "bg" ? "Треньорски състав" : "Coaching staff"}
              </h2>
              <span className="label-mono">{coaches.length}</span>
            </div>
            {coaches.length === 0 ? (
              <div className="text-white/35 text-xs font-mono tracking-widest py-8 text-center" data-testid="academy-no-coaches">
                {lang === "bg" ? "Все още няма треньори. Покани първия." : "No coaches yet. Invite your first."}
              </div>
            ) : (
              <div className="space-y-2">
                {coaches.map((c) => (
                  <div key={c.id} className="surface-1 p-3 flex items-center gap-4" data-testid={`academy-coach-${c.id}`}>
                    <Avatar name={c.name} src={c.photo_url} size="md" />
                    <div className="flex-1 min-w-0">
                      <div className="font-oswald uppercase tracking-tight text-base truncate">{c.name}</div>
                      <div className="text-[11px] text-white/45 font-mono flex items-center gap-3 mt-0.5">
                        {c.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.city}</span>}
                        {c.specialty && <span className="chip-badge text-[9px] py-0.5 px-2">{c.specialty}</span>}
                        {c.hourly_rate != null && <span className="text-pitch">€{c.hourly_rate}/h</span>}
                      </div>
                    </div>
                    {(c.badges || []).includes("verified") && (
                      <span className="w-6 h-6 bg-pitch text-white rounded-full flex items-center justify-center text-[10px] font-bold" title="Verified">✓</span>
                    )}
                    <button
                      onClick={() => removeCoach(c.id)}
                      className="p-2 text-white/40 hover:text-red-400 transition-colors"
                      data-testid={`academy-remove-coach-${c.id}`}
                      aria-label="Remove coach"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Invite form */}
            <form onSubmit={invite} className="mt-5 pt-4 border-t border-white/10 space-y-2" data-testid="academy-invite-form">
              <div className="label-mono mb-1">// {lang === "bg" ? "Покани треньор" : "Invite coach"}</div>
              <div className="flex gap-2 flex-col sm:flex-row">
                <div className="relative flex-1">
                  <Mail className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder={lang === "bg" ? "коуч@email.com" : "coach@email.com"}
                    className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-3 py-2.5 focus:border-pitch outline-none rounded"
                    data-testid="academy-invite-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={inviteBusy}
                  className="btn-primary px-4 py-2.5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="academy-invite-submit"
                >
                  {inviteBusy ? <Sparkles className="w-4 h-4 animate-pulse" /> : <UserPlus className="w-4 h-4" />}
                  {lang === "bg" ? "Покани" : "Invite"}
                </button>
              </div>
              {inviteError && <div className="text-red-400 text-xs font-mono" data-testid="academy-invite-error">{inviteError}</div>}
              {inviteOk && <div className="text-pitch text-xs font-mono" data-testid="academy-invite-ok">{inviteOk}</div>}
              <div className="text-[10px] text-white/30 font-mono">
                {lang === "bg"
                  ? "Треньорът трябва вече да има акаунт в ONTHEPITCH."
                  : "The coach must already have an ONTHEPITCH account."}
              </div>
            </form>
          </div>

          {/* Upcoming sessions */}
          <div className="card-sharp p-5 animate-fade-up" style={{ animationDelay: "120ms", opacity: 0 }}>
            <h2 className="font-oswald uppercase text-2xl tracking-tight mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-pitch" /> {lang === "bg" ? "Предстоящо" : "Upcoming"}
            </h2>
            {upcoming.length === 0 ? (
              <div className="text-white/35 text-xs font-mono tracking-widest py-8 text-center">
                {lang === "bg" ? "Няма предстоящи сесии" : "No upcoming sessions"}
              </div>
            ) : (
              <div className="space-y-2">
                {upcoming.map((t) => (
                  <div key={t.id} className="surface-1 p-3" data-testid={`academy-session-${t.id}`}>
                    <div className="font-oswald uppercase text-sm tracking-tight">{t.title}</div>
                    <div className="text-[11px] text-white/45 font-mono mt-1 flex items-center gap-3 flex-wrap">
                      <span>{new Date(t.start_at).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      {t.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.location}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Applications inbox — Lead-Gen — Iter 28 */}
        <ApplicationsPanel
          lang={lang}
          applications={applications}
          onChange={load}
        />
      </div>
    </Layout>
  );
}

/* ---------------- Applications inbox ---------------- */
function ApplicationsPanel({ lang, applications, onChange }) {
  const [filter, setFilter] = useState("all");
  const filtered = applications.filter((a) => filter === "all" || a.status === filter);

  const setStatus = async (id, status) => {
    try {
      await api.put(`/academy/applications/${id}`, { status });
      toast.success(lang === "bg" ? "Обновено" : "Updated");
      onChange?.();
    } catch (_) {
      toast.error("Error");
    }
  };

  return (
    <div className="card-sharp p-5 animate-fade-up" style={{ animationDelay: "180ms", opacity: 0 }} data-testid="academy-applications">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-oswald uppercase text-2xl tracking-tight flex items-center gap-2">
          <Inbox className="w-5 h-5 text-pitch" /> {lang === "bg" ? "Заявки от родители" : "Inquiries inbox"}
        </h2>
        <div className="inline-flex border border-white/10 rounded overflow-hidden">
          {[
            { id: "all", bg: "Всички", en: "All" },
            { id: "new", bg: "Нови", en: "New" },
            { id: "contacted", bg: "Свързани", en: "Contacted" },
            { id: "converted", bg: "Записани", en: "Converted" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest ${filter === f.id ? "bg-pitch text-white" : "text-white/55 hover:text-white"}`}
              data-testid={`apps-filter-${f.id}`}
            >
              {lang === "bg" ? f.bg : f.en}
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="text-white/35 text-xs font-mono tracking-widest py-10 text-center" data-testid="apps-empty">
          {lang === "bg" ? "Все още няма заявки." : "No applications yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((a) => (
            <div key={a.id} className="surface-1 p-4 rounded" data-testid={`app-row-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-oswald uppercase tracking-tight text-base">{a.parent_name}</span>
                    <span className={`chip-badge ${a.status === "new" ? "gold" : ""}`}>
                      {a.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/45 font-mono flex items-center gap-3 flex-wrap mt-1">
                    {a.parent_email && (
                      <a href={`mailto:${a.parent_email}`} className="hover:text-pitch flex items-center gap-1" data-testid={`app-email-${a.id}`}>
                        <MailIcon className="w-3 h-3" /> {a.parent_email}
                      </a>
                    )}
                    {a.parent_phone && (
                      <a href={`tel:${a.parent_phone}`} className="hover:text-pitch flex items-center gap-1" data-testid={`app-phone-${a.id}`}>
                        <Phone className="w-3 h-3" /> {a.parent_phone}
                      </a>
                    )}
                    <span>{new Date(a.created_at).toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  </div>
                  {a.player_name && (
                    <div className="text-xs text-white/65 mt-2 flex flex-wrap items-center gap-2">
                      <Trophy className="w-3 h-3 text-pitch" />
                      <span className="font-oswald uppercase tracking-tight">{a.player_name}</span>
                      {a.player_age != null && <span className="text-white/40">· {a.player_age}{lang === "bg" ? " г." : "y"}</span>}
                      {a.player_level && <span className="chip-badge text-[9px]">{a.player_level}</span>}
                    </div>
                  )}
                  {a.message && (
                    <div className="text-xs text-white/70 mt-2 whitespace-pre-wrap border-l-2 border-pitch/40 pl-3">
                      {a.message}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {a.status !== "contacted" && (
                    <button
                      onClick={() => setStatus(a.id, "contacted")}
                      className="p-2 text-white/55 hover:text-pitch hover:bg-pitch/5 rounded transition-colors"
                      title={lang === "bg" ? "Маркирай като свързан" : "Mark as contacted"}
                      data-testid={`app-mark-contacted-${a.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                  )}
                  {a.status !== "converted" && (
                    <button
                      onClick={() => setStatus(a.id, "converted")}
                      className="p-2 text-white/55 hover:text-pitch hover:bg-pitch/5 rounded transition-colors"
                      title={lang === "bg" ? "Записан" : "Mark as converted"}
                      data-testid={`app-mark-converted-${a.id}`}
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                  {a.status !== "archived" && (
                    <button
                      onClick={() => setStatus(a.id, "archived")}
                      className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/5 rounded transition-colors"
                      title={lang === "bg" ? "Архивирай" : "Archive"}
                      data-testid={`app-archive-${a.id}`}
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, delay = 0 }) {
  const numeric = typeof value === "number";
  return (
    <div className="stat-tile animate-fade-up" style={{ animationDelay: `${delay}ms`, opacity: 0 }} data-testid={`academy-stat-${label}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="stat-label">{label}</div>
          <div className="stat-number mt-2">
            {numeric ? <CountUp value={value} /> : value}
          </div>
        </div>
        <div className="relative">
          <div className="absolute inset-0 bg-pitch blur-2xl opacity-25" />
          <div className="w-10 h-10 rounded-full bg-pitch/8 border border-pitch/30 flex items-center justify-center relative">
            <Icon className="w-4 h-4 text-pitch" />
          </div>
        </div>
      </div>
    </div>
  );
}
